import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authService } from "@services/authService";
import { authManager } from "@services/auth/authManager";
import { ApiError } from '@/types/api';
import { User } from '@features/auth/types';

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  loading: boolean;
  error: ApiError | null;
}

const initialState: AuthState = {
  isAuthenticated: !!localStorage.getItem('token'),
  user: null,
  token: localStorage.getItem('token'),
  loading: false,
  error: null,
};

export const login = createAsyncThunk<
  { token: string },
  { email: string; password: string },
  { rejectValue: ApiError }
>('auth/login', async (credentials, { rejectWithValue, dispatch }) => {
  try {
    const response = await authService.login(credentials);
    localStorage.setItem('token', response.token);
    
    // After successful login, fetch the user profile
    dispatch(fetchUserProfile());
    
    return response;
  } catch (error) {
    return rejectWithValue(error as ApiError);
  }
});


export const recoveryPassword = createAsyncThunk<
  { status: string },
  { email: string },
  { rejectValue: ApiError }
>('auth/recovery_password', async (credentials, { rejectWithValue, dispatch }) => {
  try {
    const response = await authService.recoveryPassword(credentials);
    
    // After successful login, fetch the user profile
    // dispatch(fetchUserProfile());
    
    return response;
  } catch (error) {
    return rejectWithValue(error as ApiError);
  }
});


export const checkCodePassword = createAsyncThunk<
  { status: string },
  { email: string,code:string },
  { rejectValue: ApiError }
>('auth/check_code_password', async (credentials, { rejectWithValue, dispatch }) => {
  try {
    const response = await authService.checkCodePassword(credentials);
    
    // After successful login, fetch the user profile
    // dispatch(fetchUserProfile());
    
    return response;
  } catch (error) {
    return rejectWithValue(error as ApiError);
  }
});


export const changeRecoveryPassword = createAsyncThunk<
  { status: string },
  { email: string,code:string,password:string,password_confirm:string },
  { rejectValue: ApiError }
>('auth/check_code_password', async (credentials, { rejectWithValue, dispatch }) => {
  try {
    const response = await authService.changePasswordRecovery(credentials);
    
    // After successful login, fetch the user profile
    // dispatch(fetchUserProfile());
    
    return response;
  } catch (error) {
    return rejectWithValue(error as ApiError);
  }
});

export const fetchUserProfile = createAsyncThunk<
  User,
  void,
  { rejectValue: ApiError }
>('auth/fetchUserProfile', async (_, { rejectWithValue }) => {
  try {
    const user:any = await authService.fetchUserProfile();
    return user.data;
  } catch (error) {
    return rejectWithValue(error as ApiError);
  }
});

export const checkAuth = createAsyncThunk<
  void,
  void,
  { rejectValue: ApiError }
>('auth/checkAuth', async (_, { rejectWithValue, dispatch }) => {
  try {
    const token = authManager.getToken();
    if (!token) {
      throw new Error('No token found');
    }
    
    // Use the fetchUserProfile thunk to get the user data
    dispatch(fetchUserProfile());
  } catch (error) {
    localStorage.removeItem('token'); // Remove o token inválido
    return rejectWithValue({
      code: 'SESSION_EXPIRED',
      message: 'Sessão expirada',
      statusCode: 401
    });
  }
});

export const logoutThunk = createAsyncThunk(
  'auth/logout',
  async () => {
    authManager.logout();
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      localStorage.removeItem('token');
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.loading = false;
        localStorage.setItem('token', action.payload.token);
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || {
          code: 'UNKNOWN_ERROR',
          message: action.error.message || 'Falha no login',
          statusCode: 400
        };
      })
      .addCase(fetchUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.user = action.payload;
        state.loading = false;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading = false;
        // Only clear auth if it's an unauthorized error
        if (action.payload?.statusCode === 401) {
          state.isAuthenticated = false;
          state.user = null;
          state.token = null;
          localStorage.removeItem('token');
        }
        state.error = action.payload || {
          code: 'UNKNOWN_ERROR',
          message: action.error.message || 'Falha ao obter perfil',
          statusCode: 400
        };
      })
      .addCase(checkAuth.rejected, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.error = {
          code: 'SESSION_EXPIRED',
          message: 'Sessão expirada',
          statusCode: 401
        };
      })
      .addCase(logoutThunk.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
